import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import SpeedIcon from "@mui/icons-material/Speed";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "stores";
import { getIsPro } from "stores/modules/app.user/selectors";
import { getAppState, getIsOwner } from "stores/modules/app.state/selectors";
import { useEffect, useState } from "react";
import {
  updateTurboRoom,
  refreshTurboRoom,
} from "stores/modules/entities.turboRooms/operations";
import { getTurboRoom } from "stores/modules/entities.turboRooms/selectors";
import CcfoliaProIcon from "./CcfoliaProIcon";
import theme from "theme";

const TurboModeSettings = () => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const isPro = useAppSelector(getIsPro);
  const isOwner = useAppSelector(getIsOwner);
  const roomId = useAppSelector((state) => getAppState(state, "roomId"));
  const turboRoom = useAppSelector((state) =>
    roomId ? getTurboRoom(state, roomId) : null
  );
  const [turboModeEnabled, setTurboModeEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [remainingTime, setRemainingTime] = useState<string>("");

  const checkExpiration = (timestamp: any) => {
    if (!timestamp) return true;

    const timestampMs = timestamp.seconds
      ? timestamp.seconds * 1000
      : timestamp;
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;

    return now - timestampMs > twentyFourHours;
  };

  const calculateRemainingTime = (timestamp: any) => {
    if (!timestamp) return "";

    const timestampMs = timestamp.seconds
      ? timestamp.seconds * 1000
      : timestamp;
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    const remainingMs = twentyFourHours - (now - timestampMs);

    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  // Update states when turboRoom changes from Redux
  useEffect(() => {
    if (turboRoom && turboRoom.enabled) {
      const expired = checkExpiration(turboRoom.timestamp);
      setIsExpired(expired);
      setTurboModeEnabled(turboRoom.enabled && !expired);
      setRemainingTime(calculateRemainingTime(turboRoom.timestamp));
    } else {
      setTurboModeEnabled(false);
      setIsExpired(false);
      setRemainingTime("");
    }
  }, [turboRoom]);

  // Update remaining time every minute
  useEffect(() => {
    if (!turboRoom || !turboRoom.enabled) return;

    const interval = setInterval(() => {
      const expired = checkExpiration(turboRoom.timestamp);
      const remaining = calculateRemainingTime(turboRoom.timestamp);

      setIsExpired(expired);
      setRemainingTime(remaining);

      if (expired) {
        setTurboModeEnabled(false);
      }
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [turboRoom]);

  const handleTurboModeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!roomId) return;

    const enabled = event.target.checked;
    setTurboModeEnabled(enabled);

    try {
      await dispatch(updateTurboRoom(roomId, enabled));
      setIsExpired(false);
    } catch (error) {
      console.error("Error updating turbo room:", error);
      setTurboModeEnabled(!enabled);
    }
  };

  const handleRefresh = async () => {
    if (!roomId) return;

    try {
      await dispatch(refreshTurboRoom(roomId));
      // Redux state will be automatically updated via onSnapshot
    } catch (error) {
      console.error("Error refreshing turbo room:", error);
    }
  };

  return (
    <>
      <Heading>
        {t("ターボモード")} <CcfoliaProIcon />
      </Heading>
      <SubTitle>{t("ネットワーク高速化")}</SubTitle>
      <Box mb={3}>
        <FormControlLabel
          control={
            <Switch
              checked={turboModeEnabled}
              onChange={handleTurboModeChange}
              disabled={!isPro || !isOwner || loading}
            />
          }
          label={t("ターボモードを有効にする")}
        />

        {turboRoom && turboRoom.enabled && (
          <Box mt={1} mb={2}>
            <Box display="flex" alignItems="center" gap="8px" mb={1}>
              {remainingTime && (
                <Typography
                  component="span"
                  variant="body2"
                  color={isExpired ? "error" : "primary"}
                >
                  {t("ターボモード有効時間")} {remainingTime}
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={!isPro || !isOwner || loading}
            >
              {t("有効時間を延長")}
            </Button>
          </Box>
        )}

        <Box display="flex" alignItems="center" gap="8px" mt={2}>
          <SpeedIcon color="primary" />
          <Typography variant="body2" color={theme.palette.text.secondary}>
            {t(
              "ターボモードはネットワークを高速化し、画像やファイルの読み込みを高速化しますが、環境によっては通常モードの方が適している場合があります。最大24時間有効です。"
            )}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

const Heading = styled(Typography).attrs({ variant: "h2" })`
  margin: 0;
  margin-bottom: 24px;
  font-size: 18px;
  font-weight: bold;
`;

const SubTitle = styled(Typography).attrs({ variant: "h2" })`
  margin: 0;
  font-size: 14px;
  font-weight: bold;
`;

export default TurboModeSettings;
